import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheck,
  faLink,
  faMoneyBillWave,
  faSync,
  faUserCheck,
  faUserPlus,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { analyticsAPI } from "../../services/analyticsService";
import { BarChart } from "../AnalyticsCharts";
import { StatCard } from "../AnalyticsWidgets";
import Loader from "../Loader";
import Styles from "../../styles/Analytics.module.css";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  percent,
} from "../../utils/analyticsUtils";

const RANGE_OPTIONS = [
  { key: "12m", label: "Last 12 months", months: 12 },
  { key: "24m", label: "Last 24 months", months: 24 },
  { key: "all", label: "All available data", all: true },
];

const getRange = (key) => {
  const option = RANGE_OPTIONS.find((item) => item.key === key);
  const end = new Date();
  const start = option.all
    ? new Date(end.getFullYear() - 4, 0, 1)
    : new Date(end.getFullYear(), end.getMonth() - option.months + 1, 1);

  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
    label: option.label,
  };
};

const hasCurrentEntitlement = (subscription) => {
  if (!subscription) return false;
  const status = String(subscription.status || "").toLowerCase();
  const isTrial = subscription.is_trial || status === "trial";
  const expiry = isTrial
    ? subscription.trial_ends_at || subscription.subscription_expires_at
    : subscription.subscription_expires_at;
  return !expiry || new Date(expiry).getTime() > Date.now();
};

const subscriptionLabel = (subscription) => {
  if (!subscription) return "Free";
  const status = String(subscription.status || "").toLowerCase();
  const isTrial = subscription.is_trial || status === "trial";
  if (isTrial && hasCurrentEntitlement(subscription)) return "Trial";
  if (status === "active" && !isTrial && hasCurrentEntitlement(subscription)) {
    return subscription.plan_type === "yearly" ? "Annual" : "Monthly";
  }
  if (status === "active") return "Expired";
  return subscription.status || "Free";
};

const subscriptionTone = (subscription) => {
  if (!subscription) return Styles.statusFree;
  const status = String(subscription.status || "").toLowerCase();
  const isTrial = subscription.is_trial || status === "trial";
  if (isTrial && hasCurrentEntitlement(subscription)) return Styles.statusTrial;
  if (status === "active" && !isTrial && hasCurrentEntitlement(subscription)) {
    return Styles.statusPro;
  }
  return Styles.statusFree;
};

const AnalyticsReferrals = () => {
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    generated_at: null,
    summary: {},
    rows: [],
    affiliates: [],
    monthly_usage: [],
  });
  const requestId = useRef(0);
  const loadRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [payoutDraft, setPayoutDraft] = useState({
    amount: "",
    currency: "USD",
    reference: "",
  });
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const load = useCallback(async () => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    const range = getRange(rangeKey);
    setLoading(true);
    setError("");
    setData({
      generated_at: null,
      summary: {},
      rows: [],
      affiliates: [],
      monthly_usage: [],
    });

    try {
      const result = await analyticsAPI.getReferrals({
        startDate: range.startDate,
        endDate: range.endDate,
        search: query,
      });
      if (requestId.current === currentRequest) {
        setData({
          generated_at: result?.generated_at || null,
          summary: result?.summary || {},
          rows: result?.rows || [],
          affiliates: result?.affiliates || [],
          monthly_usage: result?.monthly_usage || [],
        });
      }
    } catch (loadError) {
      if (requestId.current === currentRequest) {
        setError(loadError.message || "Failed to load referral analytics");
      }
    } finally {
      if (requestId.current === currentRequest) {
        setLoading(false);
      }
    }
  }, [query, rangeKey]);

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const monthlySeries = useMemo(() => {
    const grouped = new Map();
    data.monthly_usage.forEach((row) => {
      grouped.set(row.month, (grouped.get(row.month) || 0) + Number(row.uses || 0));
    });
    return Array.from(grouped.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([month, value]) => {
        const date = new Date(`${month}-01T00:00:00`);
        return {
          label: date.toLocaleDateString("en-US", { month: "short" }),
          fullLabel: date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          }),
          value,
        };
      });
  }, [data.monthly_usage]);

  const startPayout = (row) => {
    setEditingId(row.id);
    setPayoutDraft({
      amount: row.payout_amount == null ? "" : String(row.payout_amount),
      currency: row.payout_currency || "USD",
      reference: row.payout_reference || "",
    });
  };

  const savePayout = async (row, paid) => {
    setSavingId(row.id);
    setError("");

    try {
      await analyticsAPI.setReferralPayout({
        referralId: row.id,
        paid,
        amount:
          paid && payoutDraft.amount.trim() !== ""
            ? Number(payoutDraft.amount)
            : null,
        currency: payoutDraft.currency.toUpperCase(),
        reference: payoutDraft.reference.trim() || null,
      });
      setEditingId(null);
      await loadRef.current?.();
    } catch (saveError) {
      setError(saveError.message || "Failed to update payout");
    } finally {
      setSavingId(null);
    }
  };

  const handlePayoutAction = (row) => {
    if (!row.paid) {
      startPayout(row);
      return;
    }

    const amount = row.payout_amount == null
      ? "no amount"
      : formatMoney(row.payout_amount, row.payout_currency || "USD");
    const confirmed = window.confirm(
      `Mark this referral unpaid? The current payout record (${amount}) will be cleared from the referral while remaining in the audit history.`,
    );
    if (confirmed) {
      void savePayout(row, false);
    }
  };

  const summary = data.summary || {};
  const conversionRate = percent(
    summary.subscribed_referred_users,
    summary.referred_users,
  );
  const payoutTotals = Object.entries(summary.payout_totals || {});

  if (loading && data.rows.length === 0 && data.affiliates.length === 0) {
    return (
      <div className={Styles.container}>
        <Loader text="Loading referral analytics" />
      </div>
    );
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.headerRow}>
        <div>
          <h1 className={Styles.title}>Referral Analytics</h1>
          <p className={Styles.welcomeMessage}>
            Every affiliate, code, referred user, subscription conversion, and
            payout in one place.
          </p>
        </div>
        <div className={Styles.headerActions}>
          <select
            className={Styles.rangeSelect}
            value={rangeKey}
            onChange={(event) => setRangeKey(event.target.value)}
            aria-label="Referral date range"
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={Styles.refreshButton}
            onClick={load}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            Refresh
          </button>
          <button
            type="button"
            className={Styles.backButton}
            onClick={() => navigate("/analytics")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Analytics
          </button>
        </div>
      </div>

      {error && (
        <div className={Styles.errorBox} role="alert">
          {error}
        </div>
      )}

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Search Referrals</h2>
          <p className={Styles.sectionSubtitle}>
            Search by either user, email, username, or code
          </p>
        </div>
        <input
          className={Styles.searchInput}
          aria-label="Search referrals"
          placeholder="Search referrals..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className={Styles.statsGrid}>
        <StatCard
          icon={faUsers}
          label="Total Referrals"
          value={formatNumber(summary.total_referrals)}
          hint={`${formatNumber(summary.unique_referrers)} people shared codes`}
          color="#3b82f6"
          onClick={() => navigate("/analytics/users?filter=referred")}
        />
        <StatCard
          icon={faUserCheck}
          label="Subscribed Referrals"
          value={formatNumber(summary.subscribed_referred_users)}
          hint={`${conversionRate}% conversion`}
          color="#10b981"
        />
        <StatCard
          icon={faUserPlus}
          label="Monthly Referred Subscribers"
          value={formatNumber(summary.monthly_subscribed_referrals)}
          hint="Referred users who bought monthly"
          color="#f97316"
        />
        <StatCard
          icon={faUserPlus}
          label="Annual Referred Subscribers"
          value={formatNumber(summary.annual_subscribed_referrals)}
          hint="Referred users who bought annual"
          color="#fbbf24"
        />
        <StatCard
          icon={faLink}
          label="Affiliates"
          value={formatNumber(data.affiliates.length)}
          hint="Flagged, code-holding, or active referrers"
          color="#8b5cf6"
          onClick={() => navigate("/analytics/users?filter=affiliate")}
        />
        <StatCard
          icon={faMoneyBillWave}
          label="Recorded Payouts"
          value={
            payoutTotals.length
              ? payoutTotals
                  .map(([currency, value]) => formatMoney(value, currency))
                  .join(" / ")
              : "$0"
          }
          hint={`${formatNumber(summary.paid_referrals)} paid • ${formatNumber(
            summary.unpaid_referrals,
          )} unpaid`}
          color="#10b981"
        />
      </div>

      <div className={Styles.chartsGrid}>
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Monthly Code Usage</h3>
              <p className={Styles.cardSubtitle}>{getRange(rangeKey).label}</p>
            </div>
            <FontAwesomeIcon icon={faLink} />
          </div>
          {monthlySeries.length ? (
            <BarChart
              data={monthlySeries}
              color="#f97316"
              ariaLabel="Referral code usage by month"
            />
          ) : (
            <p className={Styles.emptyText}>No code usage in this range.</p>
          )}
        </div>

        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Subscription Conversion</h3>
              <p className={Styles.cardSubtitle}>Referred users who paid</p>
            </div>
            <FontAwesomeIcon icon={faUserCheck} />
          </div>
          <div className={Styles.metricList}>
            <div>
              <span>All referred users</span>
              <strong>{formatNumber(summary.referred_users)}</strong>
            </div>
            <div>
              <span>Monthly subscribers</span>
              <strong>
                {formatNumber(summary.monthly_subscribed_referrals)}
              </strong>
            </div>
            <div>
              <span>Annual subscribers</span>
              <strong>{formatNumber(summary.annual_subscribed_referrals)}</strong>
            </div>
            <div>
              <span>Conversion rate</span>
              <strong>{conversionRate}%</strong>
            </div>
          </div>
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Affiliates</h2>
          <p className={Styles.sectionSubtitle}>
            Select an affiliate to open their full profile and referral network.
          </p>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>Affiliate</th>
                <th>Email</th>
                <th>Code</th>
                <th>Code Uses</th>
                <th>Subscribed</th>
                <th>Conversion</th>
                <th>Payouts</th>
              </tr>
            </thead>
            <tbody>
              {data.affiliates.map((affiliate) => {
                const affiliateConversion = percent(
                  affiliate.subscribed_referrals,
                  affiliate.total_referrals,
                );
                const payouts = Object.entries(affiliate.payout_totals || {});
                return (
                  <tr key={affiliate.id}>
                    <td>
                      <Link
                        className={Styles.userLink}
                        to={`/analytics/users/${affiliate.id}`}
                      >
                        @{affiliate.username}
                      </Link>
                    </td>
                    <td>
                      <Link
                        className={Styles.emailLink}
                        to={`/analytics/users/${affiliate.id}`}
                      >
                        {affiliate.email}
                      </Link>
                    </td>
                    <td>{affiliate.referral_code || "—"}</td>
                    <td>{formatNumber(affiliate.total_referrals)}</td>
                    <td>{formatNumber(affiliate.subscribed_referrals)}</td>
                    <td>{affiliateConversion}%</td>
                    <td>
                      {payouts.length
                        ? payouts
                            .map(([currency, value]) =>
                              formatMoney(value, currency),
                            )
                            .join(" / ")
                        : "$0"}
                    </td>
                  </tr>
                );
              })}
              {data.affiliates.length === 0 && (
                <tr>
                  <td colSpan="7" className={Styles.emptyTableCell}>
                    No affiliates match this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Monthly Affiliate Codes</h2>
          <p className={Styles.sectionSubtitle}>
            Code usage, paid referrals, and subscribed users by month
          </p>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Affiliate</th>
                <th>Email</th>
                <th>Code Used</th>
                <th>Uses</th>
                <th>Paid</th>
                <th>Subscribed</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly_usage.map((row) => (
                <tr key={`${row.month}-${row.referrer_id}-${row.referral_code}`}>
                  <td>{row.month_label}</td>
                  <td>
                    <Link
                      className={Styles.userLink}
                      to={`/analytics/users/${row.referrer_id}`}
                    >
                      @{row.referrer_username}
                    </Link>
                  </td>
                  <td>
                    <Link
                      className={Styles.emailLink}
                      to={`/analytics/users/${row.referrer_id}`}
                    >
                      {row.referrer_email}
                    </Link>
                  </td>
                  <td>{row.referral_code || "—"}</td>
                  <td>{formatNumber(row.uses)}</td>
                  <td>{formatNumber(row.paid)}</td>
                  <td>{formatNumber(row.subscribed_users)}</td>
                </tr>
              ))}
              {data.monthly_usage.length === 0 && (
                <tr>
                  <td colSpan="7" className={Styles.emptyTableCell}>
                    No monthly code usage found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>All Referrals</h2>
          <p className={Styles.sectionSubtitle}>
            Click any email to open that user’s complete profile
          </p>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>Referrer</th>
                <th>Referrer email</th>
                <th>Referred user</th>
                <th>Referred email</th>
                <th>Code</th>
                <th>Date</th>
                <th>Subscription</th>
                <th>Payout</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td>
                      <Link
                        className={Styles.userLink}
                        to={`/analytics/users/${row.referrer_id}`}
                      >
                        @{row.referrer_username}
                      </Link>
                    </td>
                    <td>
                      <Link
                        className={Styles.emailLink}
                        to={`/analytics/users/${row.referrer_id}`}
                      >
                        {row.referrer_email}
                      </Link>
                    </td>
                    <td>
                      <Link
                        className={Styles.userLink}
                        to={`/analytics/users/${row.referred_id}`}
                      >
                        @{row.referred_username}
                      </Link>
                    </td>
                    <td>
                      <Link
                        className={Styles.emailLink}
                        to={`/analytics/users/${row.referred_id}`}
                      >
                        {row.referred_email}
                      </Link>
                    </td>
                    <td>{row.referral_code_used || "—"}</td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>
                      <span
                        className={`${Styles.statusBadge} ${subscriptionTone(
                          row.referred_subscription,
                        )}`}
                      >
                        {subscriptionLabel(row.referred_subscription)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${Styles.statusBadge} ${
                          row.paid ? Styles.statusPro : Styles.statusFree
                        }`}
                      >
                        {row.paid ? "Paid" : "Unpaid"}
                      </span>
                      {row.payout_amount != null && (
                        <span className={Styles.topMeta}>
                          {formatMoney(
                            row.payout_amount,
                            row.payout_currency || "USD",
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={Styles.tableAction}
                        onClick={() => handlePayoutAction(row)}
                        disabled={savingId === row.id}
                      >
                        {row.paid ? "Unmark paid" : "Mark paid"}
                      </button>
                    </td>
                  </tr>
                  {editingId === row.id && (
                    <tr className={Styles.payoutRow}>
                      <td colSpan="9">
                        <div className={Styles.payoutEditor}>
                          <label>
                            Amount
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={payoutDraft.amount}
                              onChange={(event) =>
                                setPayoutDraft((draft) => ({
                                  ...draft,
                                  amount: event.target.value,
                                }))
                              }
                              placeholder="Optional"
                            />
                          </label>
                          <label>
                            Currency
                            <input
                              type="text"
                              maxLength="3"
                              value={payoutDraft.currency}
                              onChange={(event) =>
                                setPayoutDraft((draft) => ({
                                  ...draft,
                                  currency: event.target.value.toUpperCase(),
                                }))
                              }
                            />
                          </label>
                          <label className={Styles.referenceField}>
                            Reference
                            <input
                              type="text"
                              value={payoutDraft.reference}
                              onChange={(event) =>
                                setPayoutDraft((draft) => ({
                                  ...draft,
                                  reference: event.target.value,
                                }))
                              }
                              placeholder="Optional payment reference"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => savePayout(row, true)}
                            disabled={savingId === row.id}
                          >
                            <FontAwesomeIcon icon={faCheck} /> Confirm paid
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            disabled={savingId === row.id}
                          >
                            <FontAwesomeIcon icon={faXmark} /> Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan="9" className={Styles.emptyTableCell}>
                    No referrals match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className={Styles.tableNote}>
        Last updated {formatDateTime(data.generated_at)}. Payout changes are
        recorded in an audit table. Revenue from referrals reflects
        subscription records; actual affiliate commission terms can be added as
        payout amounts and references.
      </p>
    </div>
  );
};

export default AnalyticsReferrals;
